import React, { useEffect, useState } from 'react';
import { supabase } from '../../../utils/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { Package, Truck, CheckCircle, Clock, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

const OrderHistory = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    // Repair Modal State
    const [repairOrder, setRepairOrder] = useState(null);
    const [availableVariants, setAvailableVariants] = useState({}); // { itemId: [variants] }
    const [selectedVariants, setSelectedVariants] = useState({}); // { itemId: variantId }
    const [repairLoading, setRepairLoading] = useState(false);

    useEffect(() => {
        if (user) {
            fetchOrders();
            // Check if user is admin
            supabase.from('user_profiles').select('role').eq('id', user.id).single()
                .then(({ data }) => {
                    setIsAdmin(data?.role === 'admin' || data?.role === 'studio_admin');
                });
        }
    }, [user]);

    const fetchOrders = async () => {
        try {
            const { data, error } = await supabase
                .from('merch_orders')
                .select(`*, merch_order_items (*)`)
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setOrders(data || []);
        } catch (e) {
            console.error('Error fetching orders', e);
        } finally {
            setLoading(false);
        }
    };

    const statusColors = {
        pending: 'text-yellow-500 bg-yellow-500/10',
        paid: 'text-blue-500 bg-blue-500/10',
        fulfillment: 'text-purple-500 bg-purple-500/10',
        shipped: 'text-indigo-500 bg-indigo-500/10',
        delivered: 'text-green-500 bg-green-500/10',
        cancelled: 'text-red-500 bg-red-500/10',
    };

    const statusIcons = {
        pending: <Clock className="w-4 h-4" />,
        paid: <CheckCircle className="w-4 h-4" />,
        fulfillment: <Package className="w-4 h-4" />,
        shipped: <Truck className="w-4 h-4" />,
        delivered: <CheckCircle className="w-4 h-4" />,
        cancelled: <XCircle className="w-4 h-4" />,
    };

    // --- Repair Logic: Open Modal ---
    const openRepairModal = async (e, order) => {
        e.stopPropagation();
        setRepairOrder(order);
        setRepairLoading(true);
        setAvailableVariants({});
        setSelectedVariants({});

        try {
            const { data: { session } } = await supabase.auth.getSession();
            const variantsMap = {};
            const initialSelection = {};

            // Fetch variants for each item
            for (const item of order.merch_order_items) {
                if (item.printful_product_id) {
                    const prodUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/merch-api/products/${item.printful_product_id}`;
                    const res = await fetch(prodUrl, {
                        headers: {
                            'Authorization': `Bearer ${session?.access_token}`,
                            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                        }
                    });
                    const data = await res.json();
                    if (data.result && data.result.sync_variants) {
                        variantsMap[item.id] = data.result.sync_variants;

                        // Try to auto-select if name matches
                        const exactMatch = data.result.sync_variants.find(v => v.name === item.name);
                        if (exactMatch) initialSelection[item.id] = exactMatch.variant_id || exactMatch.id;
                    }
                }
            }
            setAvailableVariants(variantsMap);
            setSelectedVariants(initialSelection);
        } catch (err) {
            console.error('Error fetching variants', err);
            alert('Failed to load product details.');
            setRepairOrder(null);
        } finally {
            setRepairLoading(false);
        }
    };

    // --- Repair Logic: Submit ---
    const submitRepair = async () => {
        if (!repairOrder) return;
        setRepairLoading(true);

        try {
            const { data: { session } } = await supabase.auth.getSession();

            // Build corrections array - use item.id (DB row) for reliable matching
            const corrections = [];
            repairOrder.merch_order_items.forEach(item => {
                const selectedId = selectedVariants[item.id];
                if (selectedId) {
                    corrections.push({
                        item_db_id: item.id, // Database row ID for exact matching
                        item_name: item.name, // For logging only
                        new_variant_id: parseInt(selectedId)
                    });
                }
            });

            if (corrections.length === 0) {
                alert('Please select at least one variant to repair.');
                setRepairLoading(false);
                return;
            }

            const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/merch-api/sync-order`;
            const syncRes = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    order_id: repairOrder.id,
                    corrections: corrections
                })
            });

            const result = await syncRes.json();
            if (result.success || result.status === 'fulfillment') {
                alert(`Success! Order repaired and synced. Check your email.`);
                setRepairOrder(null);
                fetchOrders();
            } else {
                let errorMsg = result.details ? `${result.error}: ${result.details}` : (result.message || result.error || 'Unknown error');
                if (result.debug_info && Array.isArray(result.debug_info)) {
                    errorMsg += `\n\nDebug Info:\n${result.debug_info.join('\n')}`;
                }
                alert(`Repair failed: ${errorMsg}`);
            }

        } catch (err) {
            console.error(err);
            alert('Error submitting repair.');
        } finally {
            setRepairLoading(false);
        }
    };

    // --- Standard Sync Logic ---
    const handleSync = async (e, order) => {
        e.stopPropagation();
        if (confirm('Check payment status and retry order processing?')) {
            try {
                const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/merch-api/sync-order`;
                const { data: { session } } = await supabase.auth.getSession();
                const res = await fetch(functionUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${session?.access_token}`,
                        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ order_id: order.id })
                });

                const result = await res.json();
                if (result.success || result.status === 'fulfillment') {
                    alert(result.message || 'Order synced successfully');
                    fetchOrders();
                } else {
                    let errorMsg = result.details ? `${result.error}: ${result.details}` : (result.message || result.error || 'Unknown error');
                    if (result.debug_info && Array.isArray(result.debug_info)) {
                        errorMsg += `\n\nDebug Info server said:\n${result.debug_info.join('\n')}`;
                    }
                    alert(`Sync failed: ${errorMsg}`);
                }
            } catch (err) {
                console.error(err);
                alert('Error syncing order');
            }
        }
    };


    if (loading) return <div className="p-8 text-center text-muted-foreground">Loading orders...</div>;

    if (orders.length === 0) {
        return (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Package className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No orders yet</h3>
                <p className="text-muted-foreground mb-4">Check out our Merch Store to get some cool gear!</p>
                <a href="/merch" className="px-6 py-2 bg-primary text-primary-foreground rounded-lg inline-block">Visit Store</a>
            </div>
        );
    }

    return (
        <div className="space-y-6 relative">
            <h2 className="text-2xl font-bold text-foreground">Order History</h2>
            <div className="space-y-4">
                {orders.map(order => {
                    const isPending = order.status === 'pending';
                    const orderDate = new Date(order.created_at);
                    const isOld = (new Date() - orderDate) > 24 * 60 * 60 * 1000;
                    let displayStatus = order.status;
                    if (isPending && isOld) displayStatus = 'failed';
                    else if (isPending) displayStatus = 'pending';

                    const itemsTotal = order.merch_order_items.reduce((sum, item) => sum + (item.retail_price * item.quantity), 0);
                    const shippingCost = order.total_amount - itemsTotal;

                    return (
                        <div key={order.id} className={`bg-card border border-border rounded-lg overflow-hidden ${displayStatus === 'failed' ? 'opacity-60' : ''}`}>
                            <div
                                className="p-6 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
                                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                            >
                                <div className="flex items-center space-x-4">
                                    <div className={`p-2 rounded-full ${statusColors[displayStatus === 'failed' ? 'cancelled' : order.status] || 'bg-muted'}`}>
                                        {statusIcons[displayStatus === 'failed' ? 'cancelled' : order.status] || <Package />}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-foreground">Order #{order.printful_order_id || order.id.slice(0, 8)}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {orderDate.toLocaleDateString()} • {order.merch_order_items.length} items
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-6">
                                    <span className="font-bold text-foreground">${(order.total_amount / 100).toFixed(2)}</span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase ${statusColors[displayStatus === 'failed' ? 'cancelled' : order.status]}`}>
                                        {displayStatus === 'failed' ? 'Failed / Abandoned' : (isPending ? 'Processing' : order.status)}
                                    </span>
                                    {/* Admin-only sync/repair tools */}
                                    {isAdmin && ((order.status === 'paid' && !order.printful_order_id) || (isPending && !isOld)) && (
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={(e) => handleSync(e, order)}
                                                className="px-3 py-1 bg-secondary text-secondary-foreground text-xs rounded hover:bg-secondary/80 transition-colors"
                                            >
                                                Sync Status
                                            </button>
                                            <button
                                                onClick={(e) => openRepairModal(e, order)}
                                                className="px-3 py-1 bg-primary text-primary-foreground text-xs rounded hover:bg-primary/90 transition-colors"
                                            >
                                                Repair
                                            </button>
                                        </div>
                                    )}
                                    {expandedOrder === order.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </div>
                            </div>

                            {expandedOrder === order.id && (
                                <div className="border-t border-border p-6 bg-muted/10">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                                        <div>
                                            <h4 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Shipping Address</h4>
                                            <div className="text-sm text-foreground">
                                                <p>{order.shipping_address?.name}</p>
                                                <p>{order.shipping_address?.address1}</p>
                                                <p>{order.shipping_address?.city}, {order.shipping_address?.state_code} {order.shipping_address?.zip}</p>
                                                <p>{order.shipping_address?.country_code}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Tracking</h4>
                                            {order.tracking_url ? (
                                                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center">
                                                    <Truck className="w-4 h-4 mr-2" />
                                                    Track Package
                                                </a>
                                            ) : (
                                                <p className="text-sm text-muted-foreground">
                                                    {isPending ? 'Payment validation pending.' : 'Tracking information will be available once shipped.'}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <h4 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Order Summary</h4>
                                    <div className="space-y-3 mb-4">
                                        {order.merch_order_items.map(item => (
                                            <div key={item.id} className="flex items-center justify-between bg-background p-3 rounded border border-border">
                                                <div className="flex items-center space-x-3">
                                                    <img src={item.thumbnail_url} alt={item.name} className="w-12 h-12 rounded bg-muted object-cover" />
                                                    <div>
                                                        <p className="font-medium text-foreground">{item.name}</p>
                                                        <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                                                    </div>
                                                </div>
                                                <div className="font-semibold text-foreground">
                                                    ${(item.retail_price / 100 * item.quantity).toFixed(2)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex flex-col space-y-2 pt-4 border-t border-border">
                                        <div className="flex justify-between text-sm text-muted-foreground">
                                            <span>Subtotal</span>
                                            <span>${(itemsTotal / 100).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm text-muted-foreground">
                                            <span>Shipping</span>
                                            <span>${(shippingCost / 100).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between font-bold text-foreground text-lg pt-2">
                                            <span>Total</span>
                                            <span>${(order.total_amount / 100).toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* REPAIR MODAL */}
            {repairOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl font-bold mb-4">Repair Order #{repairOrder.printful_order_id || repairOrder.id.slice(0, 8)}</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            The variant IDs for this order seem incorrect. Please match each item to the correct Printful variant below.
                        </p>

                        <div className="space-y-6">
                            {repairOrder.merch_order_items.map(item => (
                                <div key={item.id} className="border border-border rounded p-4">
                                    <div className="flex items-center gap-4 mb-3">
                                        <img src={item.thumbnail_url} className="w-12 h-12 rounded bg-muted object-cover" />
                                        <div>
                                            <p className="font-bold">{item.name}</p>
                                            <p className="text-xs text-muted-foreground">Stored Variant ID: {item.printful_variant_id}</p>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Select Correct Variant:</label>
                                        <select
                                            className="w-full p-2 rounded bg-muted border border-border"
                                            value={selectedVariants[item.id] || ''}
                                            onChange={(e) => setSelectedVariants(prev => ({ ...prev, [item.id]: e.target.value }))}
                                        >
                                            <option value="">-- Select Variant --</option>
                                            {(availableVariants[item.id] || []).map(v => (
                                                <option key={v.id} value={v.variant_id || v.id}>
                                                    {v.name} (Catalog ID: {v.variant_id || 'N/A'}, Sync ID: {v.id})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                onClick={() => setRepairOrder(null)}
                                className="px-4 py-2 rounded bg-muted hover:bg-muted/80"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitRepair}
                                disabled={repairLoading}
                                className="px-4 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            >
                                {repairLoading ? 'Syncing...' : 'Submit Repair & Sync'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderHistory;
